import React, { useState } from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  displayPrice,
  isPriceVariationsEnabled,
  requireListingImage,
} from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { listingRatingMaybe } from '../../util/reviewStats';
import { isBookingProcessAlias } from '../../transactions/transaction';

import {
  AspectRatioWrapper,
  IconReviewStar,
  IconUserCircle,
  NamedLink,
  ResponsiveImage,
  ListingCardThumbnail,
  SwipeGallery,
  WishlistButton,
} from '../../components';
import ListingBadge from '../ListingBadge/ListingBadge';
import { badgesForListing } from '../../util/listingBadges';

import css from './ListingCard.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  const { price, publicData, config, intl, listingTypeConfig } = props;
  const showPrice = displayPrice(listingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, listingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);

  const priceValue = <span className={css.priceValue}>{formattedPrice}</span>;
  const pricePerUnit = isBookable ? (
    <span className={css.perUnit}>
      <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
    </span>
  ) : (
    ''
  );

  return (
    <div className={css.price} title={priceTitle}>
      {hasMultiplePriceVariants ? (
        <FormattedMessage
          id="ListingCard.priceStartingFrom"
          values={{ priceValue, pricePerUnit }}
        />
      ) : (
        <FormattedMessage id="ListingCard.price" values={{ priceValue, pricePerUnit }} />
      )}
    </div>
  );
};

const RatingMaybe = props => {
  const { listing, intl } = props;
  const rating = listingRatingMaybe(listing);
  if (!rating) {
    return null;
  }

  const average = intl.formatNumber(rating.average, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

  return (
    <div className={css.rating}>
      <span className={css.visuallyHidden}>
        <FormattedMessage id="ListingCard.rating" values={{ average, count: rating.count }} />
      </span>
      <span className={css.ratingVisual} aria-hidden="true">
        <IconReviewStar rootClassName={css.ratingStar} />
        {average}
        <span>({rating.count})</span>
      </span>
    </div>
  );
};

/**
 * ListingCardImage
 * Component responsible for rendering the image part of the listing card.
 * It renders a swipeable gallery when the listing has several images, otherwise the first
 * image with lazy loading, or a stylized placeholder if images are disabled for the listing type.
 * Also wraps the image in a fixed aspect ratio container for consistent layout.
 * @component
 * @param {Object} props
 * @param {Object} props.currentListing listing entity with image data
 * @param {Object} props.listingLinkParams { id, slug } of the listing page
 * @param {boolean} props.enableGallery whether several images may render as a gallery
 * @param {string} props.title listing title for alt text
 * @param {string} props.renderSizes img/srcset size rules
 * @param {number} props.aspectWidth aspect ratio width
 * @param {number} props.aspectHeight aspect ratio height
 * @param {string} props.variantPrefix image variant prefix (e.g. "listing-card")
 * @param {boolean} props.showListingImage whether to show actual listing image or not
 * @param {Object?} props.style the background color for the listing card with no image
 * @param {string?} props.badgeLabel resolved badge label rendered over the image, if any
 * @returns {JSX.Element} listing image with fixed aspect ratio or fallback preview
 */
const ListingCardImage = props => {
  const {
    currentListing,
    listingLinkParams,
    enableGallery,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    variantPrefix,
    showListingImage,
    style,
    badgeLabel,
  } = props;

  const images = currentListing.images || [];
  const firstImage = images.length > 0 ? images[0] : null;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];
  const badge = badgeLabel ? <ListingBadge className={css.badge} label={badgeLabel} /> : null;

  // The photo is never in the server-rendered HTML - lazyLoadWithDimensions measures the box
  // before it renders anything - so the box sits empty until hydration and the download are both
  // done. It shimmers until then, and the photo fades in over it. A photo that fails is an answer
  // too: waiting on it would leave the shimmer running forever.
  const [imageLoaded, setImageLoaded] = useState(false);
  const stopWaitingForImage = () => setImageLoaded(true);
  const isImagePending = variants.length > 0 && !imageLoaded;

  // The title's link covers the card, so the gallery sits above it to catch swipes and repeats
  // the link for taps. Keyboard and screen-reader users already have the title link.
  if (showListingImage && enableGallery && images.length > 1) {
    return (
      <SwipeGallery
        className={classNames(css.aspectRatioWrapper, css.gallery)}
        images={images}
        variants={variants}
        sizes={renderSizes}
        aspectWidth={aspectWidth}
        aspectHeight={aspectHeight}
        title={title}
        showArrows
        showDots
        renderSlides={slides => (
          <NamedLink
            className={css.galleryLink}
            name="ListingPage"
            params={listingLinkParams}
            tabIndex={-1}
            aria-hidden="true"
          >
            {slides}
          </NamedLink>
        )}
      >
        {badge}
      </SwipeGallery>
    );
  }

  // Render the listing image only if listing images are enabled in the listing type
  return showListingImage ? (
    <AspectRatioWrapper
      className={classNames(css.aspectRatioWrapper, { [css.shimmer]: isImagePending })}
      width={aspectWidth}
      height={aspectHeight}
    >
      <LazyImage
        rootClassName={css.rootForImage}
        className={classNames({ [css.imageLoading]: isImagePending })}
        alt={title}
        image={firstImage}
        variants={variants}
        sizes={renderSizes}
        onLoad={stopWaitingForImage}
        onError={stopWaitingForImage}
      />
      {badge}
    </AspectRatioWrapper>
  ) : (
    <ListingCardThumbnail
      style={style}
      listingTitle={title}
      className={css.aspectRatioWrapper}
      width={aspectWidth}
      height={aspectHeight}
    />
  );
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @param {boolean?} props.enableGallery off where the cards already scroll sideways, as in a carousel
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();

  const {
    className,
    rootClassName,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
    enableGallery = true,
    currentUser,
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData } = currentListing.attributes;
  const slug = createSlug(title);

  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;
  const isOwnListing = currentUser?.id?.uuid === author?.id?.uuid;

  // At most one badge per card. The resolver returns every badge that qualifies in
  // priority order; the cap is a rendering decision, so it lives here.
  const badge = badgesForListing(currentListing, author)[0];
  const badgeLabel = badge ? intl.formatMessage({ id: badge.labelKey }) : null;

  const { listingType, cardStyle } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  // Sets the listing as active in the search map when hovered (if the search map is enabled)
  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  return (
    <div className={classes} {...setActivePropsMaybe}>
      <ListingCardImage
        renderSizes={renderSizes}
        title={title}
        currentListing={currentListing}
        listingLinkParams={{ id, slug }}
        enableGallery={enableGallery}
        config={config}
        aspectWidth={aspectWidth}
        aspectHeight={aspectHeight}
        variantPrefix={variantPrefix}
        style={cardStyle}
        showListingImage={showListingImage}
        badgeLabel={badgeLabel}
      />
      {showListingImage ? (
        <WishlistButton
          listingId={currentListing.id}
          currentUser={currentUser}
          isOwnListing={isOwnListing}
          position="right"
        />
      ) : null}
      <div className={css.info}>
        <div className={css.mainInfo}>
          {!showListingImage && badgeLabel ? (
            <ListingBadge className={css.badgeInline} label={badgeLabel} />
          ) : null}
          {/* The title is the card's link and stretches over the whole card, so the heart and the
              author link are its only other controls. Without a photo the title is on the
              placeholder, so the link carries it as its label. */}
          <NamedLink
            className={css.title}
            name="ListingPage"
            params={{ id, slug }}
            aria-label={showListingImage ? undefined : title}
          >
            {showListingImage
              ? richText(title, {
                  longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                  longWordClass: css.longWord,
                })
              : null}
          </NamedLink>
          <RatingMaybe listing={currentListing} intl={intl} />
          <PriceMaybe
            price={price}
            publicData={publicData}
            config={config}
            intl={intl}
            listingTypeConfig={foundListingTypeConfig}
          />
          {showAuthorInfo && author.id ? (
            <NamedLink className={css.authorInfo} name="ProfilePage" params={{ id: author.id.uuid }}>
              <IconUserCircle rootClassName={css.authorIcon} />
              <span className={css.authorName}>
                <FormattedMessage id="ListingCard.author" values={{ authorName }} />
              </span>
            </NamedLink>
          ) : null}
        </div>
      </div>
    </div>
  );
};

/**
 * A placeholder shaped like a ListingCard, for results that are on their way.
 *
 * It borrows the card's own layout classes so the grid keeps its geometry: the same aspect ratio
 * box, the same info block metrics. aria-hidden because there is nothing here to read out - the
 * page header is what announces that results are loading.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @returns {JSX.Element} placeholder card
 */
export const ListingCardSkeleton = props => {
  const config = useConfiguration();
  const { className, rootClassName } = props;
  const { aspectWidth = 1, aspectHeight = 1 } = config.layout.listingImage;
  const classes = classNames(rootClassName || css.root, css.skeleton, className);

  return (
    <div className={classes} aria-hidden="true">
      <AspectRatioWrapper
        className={classNames(css.aspectRatioWrapper, css.shimmer)}
        width={aspectWidth}
        height={aspectHeight}
      />
      <div className={css.info}>
        <div className={css.mainInfo}>
          <div className={classNames(css.skeletonLine, css.skeletonTitle, css.shimmer)} />
          <div className={classNames(css.skeletonLine, css.skeletonPrice, css.shimmer)} />
          <div className={classNames(css.skeletonLine, css.skeletonAuthor, css.shimmer)} />
        </div>
      </div>
    </div>
  );
};

export default ListingCard;
